import { consume } from "@ndn/endpoint";
import { Forwarder } from "@ndn/fw";
import { Certificate, CertNaming } from "@ndn/keychain";
import { Interest, NameMap } from "@ndn/packet";
class Cache {
    table = new NameMap();
    nextCleanup = 0;
    positiveTtl;
    negativeTtl;
    cleanupInterval;
    constructor({ positiveTtl = 3600_000, negativeTtl = 10_000, cacheCleanupInterval = 300_000, }) {
        this.positiveTtl = positiveTtl;
        this.negativeTtl = negativeTtl;
        this.cleanupInterval = cacheCleanupInterval;
    }
    lookup(name) {
        const now = this.cleanup();
        const entry = this.table.get(name);
        if (entry && entry.expire < now) {
            this.table.delete(name);
            return undefined;
        }
        return entry;
    }
    addPositive(cert) {
        const now = this.cleanup();
        const entry = {
            expire: now + this.positiveTtl,
            cert,
        };
        this.table.set(cert.name, entry);
        this.table.set(CertNaming.toKeyName(cert.name), entry);
    }
    addNegative(name) {
        const now = this.cleanup();
        this.table.set(name, {
            expire: now + this.negativeTtl,
        });
    }
    cleanup() {
        const now = Date.now();
        if (this.nextCleanup < now) {
            for (const [key, { expire }] of this.table) {
                if (expire < now) {
                    this.table.delete(key);
                }
            }
            this.nextCleanup = now + this.cleanupInterval;
        }
        return now;
    }
}
const cacheMap = new WeakMap();
/** Fetch certificates from network. */
export class CertFetcher {
    constructor(opts) {
        const { owner: ownerInput, cOpts, interestLifetime, } = opts;
        this.cOpts = {
            describe: "trust-schema CertFetcher",
            ...cOpts,
        };
        if (interestLifetime !== undefined) {
            this.cOpts.modifyInterest = {
                lifetime: interestLifetime,
            };
        }
        const owner = ownerInput ?? cOpts?.fw ?? Forwarder.getDefault();
        let cache = cacheMap.get(owner);
        if (!cache) {
            cache = new Cache(opts);
            cacheMap.set(owner, cache);
        }
        this.cache = cache;
    }
    cOpts;
    cache;
    /**
     * Fetch certificates from network by certificate name or key name.
     * Upon successful retrieval, yields the certificate.
     * Upon unsuccessful retrieval, ends the iterable without yielding.
     * Retrieval result is cached for a period of time.
     */
    async *findCerts(keyLocator) {
        const cached = this.cache.lookup(keyLocator);
        if (cached) {
            if (cached.cert) {
                yield cached.cert;
            }
            return;
        }
        const interest = new Interest(keyLocator);
        if (!CertNaming.isCertName(keyLocator)) {
            interest.canBePrefix = true;
            interest.mustBeFresh = true;
        }
        let cert;
        try {
            const data = await consume(interest, this.cOpts);
            cert = Certificate.fromData(data);
            this.cache.addPositive(cert);
        }
        catch {
            this.cache.addNegative(keyLocator);
            return;
        }
        yield cert;
    }
}
